<template>
  <b-modal
    v-model="editorShown"
    :title="chartNoteId ? 'Edit Chart Note' : 'New Chart Note'"
    centered
    size="md"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer="{ cancel }">
      <div class="d-flex justify-content-end align-items-center">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button class="ml-2" variant="primary" @click="submitChartNote">
          Save
        </b-button>
      </div>
    </template>
    <b-form-group
      label="Effective Date"
      :invalid-feedback="formErrors.effectiveDateTime.join(', ')"
      :state="formErrors.effectiveDateTime.length ? false : null"
      style="max-width: 260px"
    >
      <date-time-picker v-model="form.effectiveDateTime" />
    </b-form-group>
    <b-form-group
      label="Note"
      :invalid-feedback="formErrors.note.join(', ')"
      :state="formErrors.note.length ? false : null"
    >
      <smart-text-input
        v-model="form.note"
        :mentions.sync="form.mentions"
        :showSubmitButton="false"
        :maxLength="1000"
        focus
      />
    </b-form-group>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import chartNoteServer from "../../server/chartNote";
import notificationServer from "../../server/notification";
import { validateRequired } from "../../helpers/validation";
import { formatDateTimeSql, formatDateTimeMysql } from "../../helpers/date";
import DateTimePicker from "../controls/DateTimePicker.vue";
import SmartTextInput from "../controls/SmartTextInput.vue";

export default {
  name: "EocViewAddEditChartNoteModal",
  components: { DateTimePicker, SmartTextInput },
  props: ["chartNoteId", "eocId", "eocLocationId"],
  data() {
    return {
      form: {
        effectiveDateTime: "",
        note: "",
        mentions: []
      },
      formErrors: {
        effectiveDateTime: [],
        note: []
      }
    };
  },
  async mounted() {
    if (this.chartNoteId) {
      const chartNote = await chartNoteServer.get(this.chartNoteId);
      this.form = {
        effectiveDateTime: formatDateTimeMysql(chartNote.effectiveDateTime),
        note: chartNote.note,
        mentions: []
      };
    } else {
      this.form.effectiveDateTime = formatDateTimeMysql(
        this.getDateTimeBySelectedLocationOrEocLocation(this.eocLocationId)
      );
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    async submitChartNote() {
      if (!this.validateForm()) {
        return;
      }

      const data = {
        effectiveDateTime: formatDateTimeSql(this.form.effectiveDateTime),
        note: this.form.note,
        episodeOfCareId: this.eocId
      };

      if (!this.chartNoteId) {
        await chartNoteServer.create(data);
      } else {
        await chartNoteServer.update(this.chartNoteId, data);
      }

      if (this.form.mentions.length) {
        notificationServer.create({
          eventName: "TaggedInChartNote",
          userIds: this.form.mentions,
          primaryEntityId: this.eocId
        });
      }

      this.showAlertSuccess();
      this.$emit("onModify");
    },
    validateForm() {
      let withoutErrors = true;

      this.formErrors.effectiveDateTime = [];
      if (!validateRequired(this.form.effectiveDateTime)) {
        this.formErrors.effectiveDateTime.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.note = [];
      if (!validateRequired(this.form.note)) {
        this.formErrors.note.push("This field is required");
        withoutErrors = false;
      } else if (this.form.note.length > 1000) {
        this.formErrors.note.push(
          `This field must be 1000 characters or fewer. You entered ${this.form.note.length} characters.`
        );
        withoutErrors = false;
      }

      return withoutErrors;
    }
  },
  computed: {
    ...mapGetters("layout", ["getDateTimeBySelectedLocationOrEocLocation"]),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  }
};
</script>

<style></style>
